import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/authStore';

// Layouts
import MainLayout from './components/layout/MainLayout';
import AuthLayout from './components/layout/AuthLayout';

// Pages (lazy-loaded)
const HomePage        = lazy(() => import('./pages/HomePage'));
const SearchPage      = lazy(() => import('./pages/trips/SearchPage'));
const TripDetailPage  = lazy(() => import('./pages/trips/TripDetailPage'));
const CreateTripPage  = lazy(() => import('./pages/trips/CreateTripPage'));
const BookingsPage    = lazy(() => import('./pages/bookings/BookingsPage'));
const BookingDetailPage = lazy(() => import('./pages/bookings/BookingDetailPage'));
const ProfilePage     = lazy(() => import('./pages/profile/ProfilePage'));
const LoginPage       = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage    = lazy(() => import('./pages/auth/RegisterPage'));
const NotFoundPage    = lazy(() => import('./pages/NotFoundPage'));

// ─── QueryClient ─────────────────────────────────────────────

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 1000 * 60 * 2,       // 2 minutes
      gcTime: 1000 * 60 * 5,           // 5 minutes
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

// ─── Guards ──────────────────────────────────────────────────

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

const PublicOnlyRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated ? <Navigate to="/" replace /> : <>{children}</>;
};

// ─── Fallback ────────────────────────────────────────────────

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="spinner w-8 h-8" />
  </div>
);

// ─── App ─────────────────────────────────────────────────────

export default function App() {
  const logout = useAuthStore((s) => s.logout);

  // Écouter l'événement de déconnexion forcée (refresh token expiré)
  useEffect(() => {
    const handler = () => logout();
    window.addEventListener('auth:logout', handler);
    return () => window.removeEventListener('auth:logout', handler);
  }, [logout]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* ── Routes publiques ── */}
            <Route element={<MainLayout />}>
              <Route path="/"            element={<HomePage />} />
              <Route path="/trips"       element={<SearchPage />} />
              <Route path="/trips/:id"   element={<TripDetailPage />} />
            </Route>

            {/* ── Routes authentifiées ── */}
            <Route element={<PrivateRoute><MainLayout /></PrivateRoute>}>
              <Route path="/trips/new"           element={<CreateTripPage />} />
              <Route path="/bookings"            element={<BookingsPage />} />
              <Route path="/bookings/:id"        element={<BookingDetailPage />} />
              <Route path="/profile"             element={<ProfilePage />} />
              <Route path="/profile/:id"         element={<ProfilePage />} />
            </Route>

            {/* ── Routes auth (non connecté seulement) ── */}
            <Route element={<PublicOnlyRoute><AuthLayout /></PublicOnlyRoute>}>
              <Route path="/login"    element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
            </Route>

            {/* ── 404 ── */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            borderRadius: '12px',
            fontSize: '14px',
            fontFamily: 'Inter, sans-serif',
          },
          success: { iconTheme: { primary: '#16A34A', secondary: '#fff' } },
          error:   { iconTheme: { primary: '#DC2626', secondary: '#fff' } },
        }}
      />

      {/* React Query Devtools (dev uniquement) */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
